import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { gradeApi } from '../../api.js';
import { Loading } from '../../components/Loading.js';
import SideBar from '../../components/SideBar';
import CourseManagement from '../../components/Teacher/TeacherAssignments/TeacherAssignmentSettings/CourseManagement';
import { FiMousePointer } from 'react-icons/fi';

const ClassSettings = () => {
  const { class_id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rosterImported, setRosterImported] = useState(false);

  // This function will be passed down to CourseManagement then to AddDropBig
  const handleRosterImported = () => {
    console.log('Roster imported');
    setRosterImported(true);
  };

  // Handle back button click
  const handleBackClick = (e) => {
    e.preventDefault();
    navigate(`/tclasses/${class_id}/tassignments`);
  };

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const response = await gradeApi.get({
          path: `/api/courses/${class_id}/`
        });
        setCourse(response.data);
      } catch (error) {
        console.error('Error fetching course:', error);
        setError('Failed to load course data');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [class_id]);

  if (loading) {
    return <Loading text="Loading class settings..." />;
  }

  if (error) {
    return <div className="p-4">{error}</div>;
  }

  if (!course) {
    return <div className="p-4">Course not found</div>;
  }

  return (
    <div className="flex h-screen bg-white">
      <SideBar courseId={class_id} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-8 py-6 bg-white  shadow-sm">
          <div className="flex items-center mb-4">
            <div className="flex items-center">
              <Link to={`/tclasses/${class_id}/tassignments`} onClick={handleBackClick}>
                <div className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center mr-3 hover:bg-gray-100 transition-colors ${
                      rosterImported ? 'relative' : ''
                    }`}
                    style={
                      rosterImported
                        ? {
                            position: 'relative',
                            filter: 'drop-shadow(0 0 8px rgba(228, 177, 122, 0.6))',
                            animation: 'subtle-pulse 2.5s infinite'
                          }
                        : {}
                    }
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 12L6 8L10 4"
                        stroke="black"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 8H14"
                        stroke="black"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    {rosterImported && (
                      <>
                        <div
                          className="pointer-container"
                          style={{ position: 'absolute', top: '23px', right: '-15px' }}
                        >
                          <FiMousePointer
                            className="mouse-pointer"
                            size={24}
                            color="#e4b17a"
                            style={{
                              filter: 'drop-shadow(0 0 3px rgba(228, 177, 122, 0.7))',
                              transform: 'rotate(-5deg)'
                            }}
                          />
                        </div>

                        <div
                          className="click-ripple-container"
                          style={{
                            position: 'absolute',
                            width: '32px',
                            height: '32px',
                            opacity: 0.9,
                            right: '-4px',
                            top: '18px'
                          }}
                        >
                          <div className="click-ripple"></div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center">
                    <h1 className="text-lg font-medium text-black">
                      {course?.name
                        ? course.name.length > 25
                          ? `${course.name.substring(0, 25)}...`
                          : course.name
                        : 'Class'}{' '}
                      Settings
                    </h1>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          <style jsx global>{`
            @keyframes subtle-pulse {
              0% {
                filter: drop-shadow(0 0 12px rgba(228, 177, 122, 0.4));
              }
              50% {
                filter: drop-shadow(0 0 21px rgba(228, 177, 122, 0.7));
              }
              100% {
                filter: drop-shadow(0 0 12px rgba(228, 177, 122, 0.4));
              }
            }

            .pointer-container {
              position: relative;
              animation: move-to-center 3s ease-in-out infinite;
            }

            @keyframes move-to-center {
              0% {
                transform: translate(20px, 20px);
              }
              40% {
                transform: translate(0, 0);
              }
              50% {
                transform: scale(0.9);
              }
              60% {
                transform: translate(0, 0) scale(1);
              }
              100% {
                transform: translate(20px, 20px);
              }
            }

            .mouse-pointer {
              filter: drop-shadow(0 0 1px rgba(228, 177, 122, 0.3));
              animation: pulse-opacity 2s ease-in-out infinite;
            }

            @keyframes pulse-opacity {
              0%,
              100% {
                opacity: 0.8;
              }
              50% {
                opacity: 1;
              }
            }

            .click-ripple-container {
              position: relative;
              animation: click-timing 3s infinite;
            }

            @keyframes click-timing {
              0%,
              45%,
              65%,
              100% {
                opacity: 0;
              }
              50%,
              55% {
                opacity: 1;
              }
            }

            .click-ripple {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(228, 177, 122, 0.6);
              border-radius: 50%;
              transform: scale(0);
              animation: click-ripple 1.5s infinite;
            }

            @keyframes click-ripple {
              0%,
              100% {
                transform: scale(0);
                opacity: 0;
              }
              10% {
                transform: scale(0);
                opacity: 0.8;
              }
              50% {
                transform: scale(10);
                opacity: 0;
              }
            }
          `}</style>
        </div>

        <div className="px-8">
          {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

          {loading ? (
            <Loading />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <CourseManagement
                course={course}
                class_id={class_id}
                onRosterImported={handleRosterImported}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassSettings;
